import { useEffect, useState } from 'react';
import {
  CODE_LENGTH,
  MAX_CALL_SIGN_LENGTH,
  formatPence,
  isValidCodeShape,
  normalizeCode,
} from '@quiz/shared';
import type { PublicEvent } from '@quiz/shared';
import { api, ApiError } from '../lib/api.js';
import { saveSession, type PlayerSession } from '../lib/session.js';
import { Screen, Button, Card, Banner } from '../components/ui.js';

interface CurrentEvent {
  id: string;
  name: string;
  entryFeePence: number;
  isFree: boolean;
  requiresCode: boolean;
  isPractice: boolean;
  hasFinished: boolean;
}

export function JoinScreen({
  event: chosen,
  savedCode,
  onJoined,
  onBack,
}: {
  /** Picked on the home page. Null falls back to whatever the URL is offering. */
  event?: PublicEvent | null;
  savedCode?: string | null;
  onJoined: (s: PlayerSession) => void;
  onBack?: () => void;
}) {
  const [event, setEvent] = useState<CurrentEvent | null>(
    chosen
      ? {
          id: chosen.id,
          name: chosen.name,
          entryFeePence: chosen.entryFeePence,
          isFree: chosen.isFree,
          requiresCode: chosen.requiresCode,
          isPractice: chosen.isPractice,
          hasFinished: false,
        }
      : null,
  );
  // Pre-fillable from a QR link: /?code=ABC234
  const [code, setCode] = useState(() =>
    // A QR link wins, then whatever this phone already had saved for this event.
    normalizeCode(new URLSearchParams(location.search).get('code') ?? savedCode ?? ''),
  );
  const [callSign, setCallSign] = useState('');
  const [available, setAvailable] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (chosen) return;
    api<CurrentEvent>('/api/event/current')
      .then(setEvent)
      .catch(() => setError('There is no quiz set up right now.'));
  }, [chosen]);

  // Live uniqueness check, debounced -- nobody should discover the clash after
  // typing a code in.
  useEffect(() => {
    const name = callSign.trim();
    if (!event || name.length < 2) {
      setAvailable(null);
      return;
    }
    const id = setTimeout(() => {
      api<{ available: boolean }>(
        `/api/callsign-available?eventId=${event.id}&callSign=${encodeURIComponent(name)}`,
      )
        .then((r) => setAvailable(r.available))
        .catch(() => setAvailable(null));
    }, 350);
    return () => clearTimeout(id);
  }, [callSign, event]);

  // The practice quiz has no codes at all, so there is nothing to validate.
  const needsCode = event?.requiresCode !== false;
  const codeOk = !needsCode || isValidCodeShape(code);
  const canSubmit = codeOk && callSign.trim().length >= 2 && available !== false && !busy;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const session = await api<PlayerSession>('/api/join', {
        method: 'POST',
        body: {
          // Say which one: the home page can show several at once.
          ...(chosen ? { eventId: chosen.id } : {}),
          ...(needsCode ? { code } : {}),
          callSign: callSign.trim(),
        },
      });
      saveSession(session);
      onJoined(session);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not join. Try again.');
      setBusy(false);
    }
  }

  // Somebody arriving after the host has ended it should be told so, not shown a
  // code box that will only refuse them.
  if (event?.hasFinished) {
    return (
      <Screen>
        <div className="flex flex-1 flex-col justify-center">
          <Card className="text-center">
            <p className="text-2xl font-bold">{event.name}</p>
            <p className="mt-3 text-muted">
              That quiz has finished. Ask the staff when the next one is on.
            </p>
          </Card>
        </div>
      </Screen>
    );
  }

  return (
    <Screen>
      {onBack && (
        <button
          onClick={onBack}
          className="mb-2 self-start rounded-lg bg-ink-2 px-3 py-2 text-sm hover:bg-ink-3"
        >
          &larr; All quizzes
        </button>
      )}

      <header className="mb-6 mt-2 text-center">
        <h1 className="text-3xl font-bold">{event?.name ?? 'Pub Quiz'}</h1>
        {event && !event.isFree && (
          <p className="mt-1 text-sm text-muted">Entry {formatPence(event.entryFeePence)}</p>
        )}
        {event?.isPractice && (
          <p className="mt-1 text-sm text-go">Free practice quiz &middot; no code needed</p>
        )}
        {event?.isFree && !event.isPractice && (
          <p className="mt-1 text-sm text-muted">Free entry tonight</p>
        )}
      </header>

      <form onSubmit={submit} className="space-y-4">
        {needsCode && (
        <Card>
          <label htmlFor="code" className="block text-xs uppercase tracking-wide text-muted">
            {event?.isFree ? 'Join code' : 'Your ticket code'}
          </label>
          <input
            id="code"
            value={code}
            onChange={(e) => setCode(normalizeCode(e.target.value).slice(0, CODE_LENGTH))}
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            inputMode="text"
            enterKeyHint="next"
            placeholder="ABC234"
            className="mt-2 w-full bg-transparent text-center font-mono text-4xl tracking-[0.3em]
              outline-none placeholder:text-ink-3"
          />
          <p className="mt-2 text-center text-xs text-muted">
            Six characters. No letter O and no number 1 &mdash; they are not in the alphabet.
          </p>
        </Card>
        )}

        <Card>
          <label htmlFor="callsign" className="block text-xs uppercase tracking-wide text-muted">
            Your name
          </label>
          <input
            id="callsign"
            value={callSign}
            onChange={(e) => setCallSign(e.target.value.slice(0, MAX_CALL_SIGN_LENGTH))}
            enterKeyHint="go"
            autoCapitalize="words"
            autoComplete="given-name"
            placeholder="Sam"
            className="mt-2 w-full bg-transparent text-2xl outline-none placeholder:text-ink-3"
          />
          {available === false ? (
            <p className="mt-2 text-sm text-stop">
              Somebody is already playing as {callSign.trim()}. Add your surname initial
              &mdash; &ldquo;{callSign.trim()} {initialHint(callSign)}&rdquo; &mdash; so the
              host can tell you apart.
            </p>
          ) : available === true ? (
            <p className="mt-2 text-sm text-go">That one is free.</p>
          ) : (
            <p className="mt-2 text-xs text-muted">
              This is what everyone sees on the leaderboard.
            </p>
          )}
        </Card>

        {error && <Banner kind="error">{error}</Banner>}

        <Button type="submit" disabled={!canSubmit}>
          {busy ? 'Joining…' : event?.isPractice ? 'Join the practice quiz' : 'Join the quiz'}
        </Button>

        {event?.isPractice && (
          <p className="pt-1 text-center text-xs text-muted">
            This one is free and just for getting the hang of it. No code, no
            charge &mdash; play as many as you like.
          </p>
        )}

        {event && !event.isFree && (
          <div className="space-y-2 pt-2">
            <p className="text-center text-sm text-muted">No code yet?</p>
            <Button
              variant="ghost"
              type="button"
              onClick={() => {
                location.href = `/pay?event=${event.id}`;
              }}
            >
              Pay {formatPence(event.entryFeePence)} to get one
            </Button>
            <p className="text-center text-sm text-muted">Or pay cash at the door.</p>
          </div>
        )}
      </form>
    </Screen>
  );
}

/**
 * Two Daves in a pub is not an edge case, it is a Thursday. Rather than just
 * refusing the name, show them what a working one looks like.
 */
function initialHint(name: string): string {
  const initial = name.trim().charAt(0).toUpperCase();
  // Anything but their own initial, so the example is visibly a surname.
  return initial === 'B' ? 'S' : 'B';
}
